import React, {
  FC,
  forwardRef,
  useImperativeHandle,
  Ref,
  RefAttributes,
  ForwardedRef,
  MutableRefObject,
} from 'react';
import clsx from 'clsx';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  MarkerProps,
  PopupProps,
  MapContainerProps,
  useMap,
  Polygon,
  PolygonProps,
  Circle,
  CircleProps,
  Polyline,
  PolylineProps,
} from 'react-leaflet';
import { Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import RotatableTriangleMarker, {
  LeafletRotatableTriangleMarkerProps,
} from './RotatableTriangleMarker';

interface MapContentProps {
  markers: MapMarker[];
  polygons?: LeafletMapPolygon[];
  circles?: LeafletMapCircle[];
  polylines?: LeafletMapPolyline[];
  triangleMarkers?: LeafletTriangleMarker[];
  ref: React.Ref<{ map: LeafletMap | null }>;
}

const MapContent: FC<MapContentProps> = forwardRef(
  (
    { markers, polygons, circles, polylines, triangleMarkers }: MapContentProps,
    ref: ForwardedRef<{ map: LeafletMap | null }>
  ) => {
    const map = useMap();

    // Expose the map object
    useImperativeHandle(ref, () => ({
      map,
    }));

    return (
      <>
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
        {markers.map((marker: MapMarker, idx: number) => (
          <Marker key={`marker-${idx}`} {...marker.markerProps}>
            {marker.popupProps && <Popup {...marker.popupProps} />}
          </Marker>
        ))}
        {polylines &&
          polylines.map((polyline: LeafletMapPolyline, idx: number) => (
            <Polyline key={`polyline-${idx}`} {...polyline} />
          ))}
        {polygons &&
          polygons.map((polygon: LeafletMapPolygon, idx: number) => (
            <Polygon key={`poly-${idx}`} {...polygon} />
          ))}
        {circles &&
          circles.map((circle: LeafletMapCircle, idx: number) => (
            <Circle key={`circle-${idx}`} {...circle}>
              {circle.popupProps && <Popup {...circle.popupProps} />}
            </Circle>
          ))}
        {triangleMarkers &&
          triangleMarkers.map(
            (triangleMarker: LeafletRotatableTriangleMarkerProps, idx: number) => (
              <RotatableTriangleMarker key={`triangle-marker-${idx}`} {...triangleMarker} />
            )
          )}
      </>
    );
  }
);

interface MapProps extends MapContainerProps, RefAttributes<{ map: LeafletMap | null }> {
  markers: MapMarker[];
  polygons?: LeafletMapPolygon[];
  circles?: CircleProps[];
  polylines?: LeafletMapPolyline[];
  triangleMarkers?: LeafletRotatableTriangleMarkerProps[];
}

const Map: FC<MapProps> = forwardRef(
  (
    { markers, polygons, circles, polylines, triangleMarkers, className, ...props }: MapProps,
    ref: ForwardedRef<{
      map: LeafletMap | null;
    }>
  ) => {
    return (
      <MapContainer className={clsx('w-full z-0 h-full', className)} tap={false} {...props}>
        <MapContent
          markers={markers}
          polygons={polygons}
          circles={circles}
          polylines={polylines}
          triangleMarkers={triangleMarkers}
          ref={ref}
        />
      </MapContainer>
    );
  }
);

export { LeafletMap };

export interface MapMarker {
  markerProps: MarkerProps;
  popupProps?: PopupProps;
}

export interface LeafletMapPolygon extends PolygonProps {}

export interface LeafletMapCircle extends CircleProps {
  popupProps?: PopupProps;
}

export interface LeafletMapPolyline extends PolylineProps {}

export interface LeafletTriangleMarker extends LeafletRotatableTriangleMarkerProps {}

export default Map;
